/**
 * src/lib/api.js — Central API client for all server communication.
 *
 * Replaces ALL localStorage-based operations with server API calls.
 * Every function hits the production server on /api/*.
 *
 * Uses httpOnly JWT cookies for auth (set by server).
 */

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Make an authenticated API request.
 * Includes credentials for httpOnly cookie transport.
 */
async function apiFetch(path, options = {}) {
    const url = `${BASE_URL}${path}`;
    const res = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const error = new Error(body.error || `API error: ${res.status}`);
        error.status = res.status;
        error.body = body;
        throw error;
    }

    return res.json();
}

// ── Auth ──

/** Register a new account with email + password. */
export async function register(email, password, displayName, cfToken) {
    return apiFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, displayName, cfToken }),
    });
}

/** Sign in with email + password. */
export async function login(email, password, cfToken) {
    return apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, cfToken }),
    });
}

/** Request password reset token. */
export async function forgotPassword(email) {
    return apiFetch('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

/** Reset password with token. */
export async function resetPassword(token, password) {
    return apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
    });
}

/** Request a nonce for wallet signature. */
export async function requestNonce(walletAddress) {
    return apiFetch(`/auth/nonce?wallet=${walletAddress}`);
}

/** Verify signed nonce and get JWT session. */
export async function verifySignature(wallet, signature, nonce) {
    return apiFetch('/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ wallet, signature, nonce }),
    });
}

/** Logout — clears JWT cookie. */
export async function logout() {
    return apiFetch('/auth/logout', { method: 'POST' });
}

/** Get current authenticated user info. */
export async function getMe() {
    return apiFetch('/auth/me');
}

// ── Bets ──

/** Place a bet on a coin/slot. */
export async function placeBet(coinMarket, slotTime, direction, amountSol) {
    return apiFetch('/bets', {
        method: 'POST',
        body: JSON.stringify({ coinMarket, slotTime, direction, amountSol }),
    });
}

/** Get current user's bet history. */
export async function getMyBets(limit = 50, offset = 0) {
    return apiFetch(`/bets/mine?limit=${limit}&offset=${offset}`);
}

/** Get recent settled rounds with open/close prices. */
export async function getSettledRounds(limit = 10) {
    return apiFetch(`/bets/settled-rounds?limit=${limit}`);
}

// ── Deposits ──

/** Get house wallet address for deposits. */
export async function getDepositWallet() {
    return apiFetch('/deposits/wallet');
}

/** Verify a SOL deposit transaction on-chain. */
export async function verifyDeposit(txSignature, amountSol, coinMarket) {
    return apiFetch('/deposits/verify', {
        method: 'POST',
        body: JSON.stringify({ txSignature, amountSol, coinMarket }),
    });
}

/** Get user's deposit history. */
export async function getMyDeposits() {
    return apiFetch('/deposits/mine');
}

// ── Admin ──

/** Get platform stats (admin only). */
export async function getAdminStats() {
    return apiFetch('/admin/stats');
}

/** Get all users (admin only). */
export async function getAdminUsers() {
    return apiFetch('/admin/users');
}

/** Get house wallet balances (admin only). */
export async function getAdminWallets() {
    return apiFetch('/admin/wallets');
}

/** Get all bets (admin only). */
export async function getAdminBets(limit = 50) {
    return apiFetch(`/admin/bets?limit=${limit}`);
}

// ── Pools ──

/** Get active pools with UP/DOWN totals. */
export async function getActivePools() {
    return apiFetch('/pools/active');
}

/** Get current user's active (unsettled) bets. */
export async function getUserActiveBets() {
    return apiFetch('/pools/user-bets');
}

/** Get current user's settlement history (ledger). */
export async function getUserSettlements(limit = 50) {
    return apiFetch(`/pools/settlements?limit=${limit}`);
}
